import type { ScheduledDetailedCopy } from './scheduled-page-copy-translations';

export const scheduledDetailedCopyFrenchItalian = {
  fr: {
    status: {
      upcoming: 'À venir',
      running: 'En cours',
      waitingBackground: 'En attente du travail en arrière-plan',
      succeeded: 'Réussi',
      failed: 'Échoué',
      skipped: 'Ignoré',
      needsAttention: 'Attention requise',
      unknownOutcome: 'Résultat inconnu',
      canceled: 'Annulé',
      malformed: 'Indisponible',
      paused: 'En pause',
    },
    editor: {
      createTitle: 'Nouvelle planification',
      editTitle: 'Modifier la planification',
      description:
        'Définissez la tâche, l’horaire, le contexte et l’autorité sans surveillance.',
      stepOf: 'Étape {step} sur 4',
      task: 'Tâche',
      timing: 'Horaire',
      execution: 'Exécution',
      review: 'Vérification',
      name: 'Nom de la planification',
      namePlaceholder: 'p. ex. bilan des performances du lundi',
      instructions: 'Instructions pour l’agent',
      instructionsPlaceholder:
        'Décrivez le résultat attendu et les emplacements où l’agent peut agir.',
      sourceChat: 'Discussion source',
      scheduleThisChat: 'Planifier cette discussion',
      sourceSnapshot:
        'Un instantané privé est enregistré lors de la sauvegarde. Les modifications ultérieures de la discussion ne changeront pas cette planification.',
      sourceUnavailable:
        'La discussion source est indisponible ou vous n’y avez plus accès.',
      sourceChatLockedNote:
        'Les planifications qui réutilisent une discussion source doivent conserver un contexte persistant et une visibilité privée.',
      recurrence: 'Répétition',
      once: 'Une fois',
      hourly: 'Toutes les heures',
      daily: 'Chaque jour',
      weekdays: 'Jours ouvrés',
      selectedWeekdays: 'Jours de la semaine choisis',
      weekly: 'Chaque semaine',
      monthly: 'Chaque mois',
      date: 'Date prévue',
      time: 'Heure locale',
      dayOfMonth: 'Jour du mois',
      whenPlaceholder: 'Demain',
      whenInvalid: 'Saisissez une date, par exemple « demain ».',
      end: 'Fin',
      never: 'Jamais',
      onDate: 'À une date',
      afterOccurrences: 'Après un nombre d’exécutions',
      occurrences: 'Exécutions',
      nextRuns: 'Prochaines exécutions',
      schedulePreview: 'Aperçu de la planification',
      noEndDate: 'Aucune date de fin.',
      runsUntil: 'S’exécute jusqu’au {date}.',
      totalRuns: '{count} exécutions au total.',
      resolvingTime: 'Le serveur vérifie cette heure locale…',
      serverTimeRequired:
        'Le serveur doit confirmer cette heure locale avant l’enregistrement.',
      contextMode: 'Contexte de discussion',
      persistent: 'Contexte persistant',
      persistentDescription:
        'Réutiliser une discussion propre à la planification pour toutes les exécutions.',
      isolated: 'Nouvelle discussion à chaque exécution',
      isolatedDescription: 'Démarrer chaque exécution avec un contexte isolé.',
      visibility: 'Visibilité',
      private: 'Privé',
      privateDescription:
        'Vous seul pouvez voir la demande complète, la conversation, l’activité des outils et le résultat.',
      workspace: 'Espace de travail',
      workspaceWarning:
        'Le partage avec l’espace de travail révèle la demande complète, la conversation, l’activité des outils et le résultat.',
      creditPolicy: 'Politique de crédits de l’espace de travail',
      creditsAvailable:
        'Les exécutions utilisent le solde et les limites actuels de l’espace de travail.',
      creditsUnavailable:
        'Cet espace de travail n’a actuellement aucun crédit disponible. La planification peut être suspendue au moment de l’exécution.',
      fullAuthorityTitle: 'Actions de l’agent',
      fullAuthorityDescription:
        'À chaque exécution, l’agent revérifie vos accès et peut effectuer des actions destructrices sans demander confirmation.',
      authorityAcknowledgment:
        'Autoriser toute action sans demander au préalable.',
      authorityRequired:
        'Autorise les actions de l’agent pour créer cette planification. Les exécutions sont sans surveillance, personne ne pourra donc les approuver ensuite.',
      recurrenceUnresolved:
        'Cette heure d’exécution n’a pas pu être confirmée. Choisis de nouveau la date et l’heure.',
      notifications: 'Notifications dans l’application',
      notificationsDescription:
        'Vous serez averti une seule fois des résultats finaux qui demandent votre attention.',
      reviewSummary:
        'Vérifiez précisément la tâche et la fréquence avant de créer la planification.',
      required: 'Ce champ est obligatoire.',
      invalid: 'Saisissez une valeur valide.',
      past: 'Choisissez une heure future.',
      endBeforeStart: 'Terminez la planification après sa première exécution.',
      draftRestored: 'Votre brouillon non enregistré a été restauré.',
      tooLong: 'Cette valeur est trop longue.',
      createDisabled:
        'Vous n’avez pas l’autorisation de créer des planifications dans cet espace de travail.',
      saved: 'Planification enregistrée',
      unsavedTitle: 'Ignorer les modifications non enregistrées ?',
      unsavedDescription:
        'Les modifications de cette planification seront perdues si vous fermez maintenant.',
      discardChanges: 'Ignorer les modifications',
    },
    detail: {
      title: 'Détails de la planification',
      cadence: 'Fréquence',
      owner: 'Propriétaire',
      timezone: 'Fuseau horaire',
      nextRun: 'Prochaine exécution',
      lastRun: 'Dernière exécution',
      prompt: 'Demande complète',
      sourceContext: 'Contexte source enregistré',
      executionMode: 'Mode d’exécution',
      recentUsage: 'Utilisation récente',
      credits: 'crédits',
      tools: 'opérations',
      history: 'Historique des exécutions',
      transcript: 'Échanges',
      resultFiles: 'Fichiers de résultat',
      noResultFiles:
        'Aucun fichier de résultat n’a été enregistré pour cette exécution.',
      noRuns: 'Aucune exécution pour le moment.',
      noTranscript:
        'Aucune conversation n’est encore disponible pour cette exécution.',
      toolActivity: 'Activité',
      toolInput: 'Détails de la tâche',
      toolResult: 'Résultat',
      noToolActivity: 'Aucune activité n’est disponible pour cette exécution.',
      loadMore: 'Charger plus',
      preview: 'Aperçu',
      sourceScheduled: 'Planifiée',
      sourceManual: 'À la demande',
      sourceWatchdog: 'Récupération',
      roleUser: 'Utilisateur',
      roleAssistant: 'Assistant IA',
      roleTool: 'Outil',
      roleSystem: 'Système',
      runNow: 'Exécuter maintenant',
      pause: 'Suspendre',
      resume: 'Reprendre',
      share: 'Partager avec l’espace de travail',
      unshare: 'Rendre privé',
      cancelRun: 'Annuler l’exécution active',
      archive: 'Archiver',
      deleteSchedule: 'Supprimer la planification',
      openThread: 'Voir la discussion',
      confirmArchive: 'Archiver cette planification ?',
      confirmArchiveDescription:
        'Les travaux futurs seront annulés. L’historique et les journaux d’audit seront conservés.',
      confirmDelete: 'Supprimer cette planification ?',
      confirmDeleteDescription:
        'Cette planification sera supprimée définitivement. Les conversations précédentes et les contenus générés resteront dans l’espace de travail.',
      confirmUnshareDescription:
        'Les membres de l’espace de travail perdront immédiatement l’accès au prompt, à la transcription, à l’activité des outils et au résultat.',
      confirmCancel: 'Annuler l’exécution active ?',
      confirmCancelDescription:
        'L’exécution s’arrêtera au prochain point d’annulation sûr.',
      actionCompleted: 'Planification mise à jour',
      permissionDenied:
        'Vous pouvez consulter cette planification, mais seuls son propriétaire ou un administrateur des planifications peuvent la modifier.',
      needsAttention:
        'Cette exécution doit être vérifiée avant de poursuivre le travail sans surveillance.',
      runDetails: 'Détails de l’exécution',
    },
    notifications: {
      completed: 'Exécution planifiée terminée',
      failed: 'Échec de l’exécution planifiée',
      attention: 'L’exécution planifiée demande votre attention',
      missed: 'L’exécution unique planifiée a été manquée',
      viewRun: 'Voir l’exécution',
      empty: 'Aucune notification d’exécution pour le moment.',
      unread: 'Non lue',
      read: 'Lue',
      caughtUp: 'Vous avez vu toutes les notifications.',
    },
    errors: {
      generic: 'Un problème est survenu. Réessayez.',
      permission: 'Vous n’avez pas l’autorisation d’effectuer cette action.',
      disabled:
        'Les agents planifiés ne sont pas encore activés dans cet espace de travail.',
      credits: 'L’espace de travail ne dispose pas de suffisamment de crédits.',
      limit:
        'Cet espace de travail a atteint sa limite de planifications actives.',
      runPending:
        'Une exécution est déjà en attente ou en cours pour cette planification.',
      invalidTime: 'Choisissez une heure future valide.',
      notFound: 'Cette planification ou cette exécution n’est plus disponible.',
    },
  },
  it: {
    status: {
      upcoming: 'In arrivo',
      running: 'In esecuzione',
      waitingBackground: 'In attesa del lavoro in background',
      succeeded: 'Riuscito',
      failed: 'Non riuscito',
      skipped: 'Saltato',
      needsAttention: 'Richiede attenzione',
      unknownOutcome: 'Esito sconosciuto',
      canceled: 'Annullato',
      malformed: 'Non disponibile',
      paused: 'In pausa',
    },
    editor: {
      createTitle: 'Nuova pianificazione',
      editTitle: 'Modifica pianificazione',
      description:
        'Imposta attività, orario, contesto e autorizzazione senza supervisione.',
      stepOf: 'Passaggio {step} di 4',
      task: 'Attività',
      timing: 'Orario',
      execution: 'Esecuzione',
      review: 'Verifica',
      name: 'Nome della pianificazione',
      namePlaceholder: 'ad es. riepilogo delle prestazioni del lunedì',
      instructions: 'Istruzioni per l’agente',
      instructionsPlaceholder:
        'Descrivi il risultato che l’agente deve produrre e dove può intervenire.',
      sourceChat: 'Chat di origine',
      scheduleThisChat: 'Pianifica questa chat',
      sourceSnapshot:
        'Al salvataggio viene acquisita un’istantanea privata. Le modifiche successive alla chat non cambieranno questa pianificazione.',
      sourceUnavailable:
        'La chat di origine non è disponibile o non hai più accesso.',
      sourceChatLockedNote:
        'Le pianificazioni che riutilizzano una chat di origine devono mantenere il contesto persistente e la visibilità privata.',
      recurrence: 'Ripetizione',
      once: 'Una volta',
      hourly: 'Ogni ora',
      daily: 'Ogni giorno',
      weekdays: 'Giorni feriali',
      selectedWeekdays: 'Giorni della settimana selezionati',
      weekly: 'Ogni settimana',
      monthly: 'Ogni mese',
      date: 'Data',
      time: 'Ora locale',
      dayOfMonth: 'Giorno del mese',
      whenPlaceholder: 'Domani',
      whenInvalid: 'Inserisci una data, ad esempio “domani”.',
      end: 'Fine',
      never: 'Mai',
      onDate: 'In una data',
      afterOccurrences: 'Dopo un numero di esecuzioni',
      occurrences: 'Esecuzioni',
      nextRuns: 'Prossime esecuzioni',
      schedulePreview: 'Anteprima pianificazione',
      noEndDate: 'Nessuna data di fine.',
      runsUntil: 'Viene eseguito fino al {date}.',
      totalRuns: '{count} esecuzioni totali.',
      resolvingTime: 'Il server sta verificando questa ora locale…',
      serverTimeRequired:
        'Il server deve confermare questa ora locale prima del salvataggio.',
      contextMode: 'Contesto della chat',
      persistent: 'Contesto persistente',
      persistentDescription:
        'Riutilizza una chat appartenente alla pianificazione in tutte le esecuzioni.',
      isolated: 'Nuova chat per ogni esecuzione',
      isolatedDescription: 'Avvia ogni esecuzione con un contesto isolato.',
      visibility: 'Visibilità',
      private: 'Privato',
      privateDescription:
        'Solo tu puoi vedere la richiesta completa, la conversazione, l’attività degli strumenti e il risultato.',
      workspace: 'Spazio di lavoro',
      workspaceWarning:
        'La condivisione con lo spazio di lavoro espone la richiesta completa, la conversazione, l’attività degli strumenti e il risultato.',
      creditPolicy: 'Politica crediti dello spazio di lavoro',
      creditsAvailable:
        'Le esecuzioni usano il saldo e i limiti attuali dello spazio di lavoro.',
      creditsUnavailable:
        'Questo spazio di lavoro non ha crediti disponibili. La pianificazione potrebbe essere sospesa durante l’esecuzione.',
      fullAuthorityTitle: 'Azioni dell’agente',
      fullAuthorityDescription:
        'A ogni esecuzione, l’agente ricontrolla i tuoi accessi e può compiere azioni distruttive senza chiedere conferma.',
      authorityAcknowledgment:
        'Consenti qualsiasi azione senza chiedere prima.',
      authorityRequired:
        'Consenti le azioni dell’agente per creare questa pianificazione. Le esecuzioni sono non presidiate, quindi nessuno potrà approvarle in seguito.',
      recurrenceUnresolved:
        'Non è stato possibile confermare questo orario di esecuzione. Scegli di nuovo data e ora.',
      notifications: 'Notifiche nell’app',
      notificationsDescription:
        'Riceverai una sola notifica per i risultati finali che richiedono la tua attenzione.',
      reviewSummary:
        'Controlla attentamente attività e frequenza prima di creare la pianificazione.',
      required: 'Questo campo è obbligatorio.',
      invalid: 'Inserisci un valore valido.',
      past: 'Scegli un orario futuro.',
      endBeforeStart: 'Termina la pianificazione dopo la prima esecuzione.',
      draftRestored: 'La tua bozza non salvata è stata ripristinata.',
      tooLong: 'Questo valore è troppo lungo.',
      createDisabled:
        'Non hai l’autorizzazione per creare pianificazioni in questo spazio di lavoro.',
      saved: 'Pianificazione salvata',
      unsavedTitle: 'Ignorare le modifiche non salvate?',
      unsavedDescription:
        'Le modifiche a questa pianificazione andranno perse se chiudi ora.',
      discardChanges: 'Ignora modifiche',
    },
    detail: {
      title: 'Dettagli della pianificazione',
      cadence: 'Frequenza',
      owner: 'Proprietario',
      timezone: 'Fuso orario',
      nextRun: 'Prossima esecuzione',
      lastRun: 'Ultima esecuzione',
      prompt: 'Richiesta completa',
      sourceContext: 'Contesto di origine salvato',
      executionMode: 'Modalità di esecuzione',
      recentUsage: 'Utilizzo recente',
      credits: 'crediti',
      tools: 'azioni',
      history: 'Cronologia delle esecuzioni',
      transcript: 'Conversazione',
      resultFiles: 'File dei risultati',
      noResultFiles:
        'Non sono stati registrati file di risultato per questa esecuzione.',
      noRuns: 'Nessuna esecuzione per ora.',
      noTranscript:
        'Non è ancora disponibile una conversazione per questa esecuzione.',
      toolActivity: 'Attività',
      toolInput: 'Dettagli del compito',
      toolResult: 'Esito',
      noToolActivity:
        'Non è disponibile alcuna attività per questa esecuzione.',
      loadMore: 'Carica altro',
      preview: 'Anteprima',
      sourceScheduled: 'Pianificata',
      sourceManual: 'Su richiesta',
      sourceWatchdog: 'Ripristino',
      roleUser: 'Utente',
      roleAssistant: 'Assistente',
      roleTool: 'Strumento',
      roleSystem: 'Sistema',
      runNow: 'Esegui ora',
      pause: 'Sospendi',
      resume: 'Riprendi',
      share: 'Condividi con lo spazio di lavoro',
      unshare: 'Rendi privato',
      cancelRun: 'Annulla esecuzione attiva',
      archive: 'Archivia',
      deleteSchedule: 'Elimina pianificazione',
      openThread: 'Visualizza thread',
      confirmArchive: 'Archiviare questa pianificazione?',
      confirmArchiveDescription:
        'Le attività future verranno annullate. La cronologia e i registri di controllo saranno conservati.',
      confirmDelete: 'Eliminare questa pianificazione?',
      confirmDeleteDescription:
        'La pianificazione verrà eliminata definitivamente. Le conversazioni precedenti e i contenuti generati resteranno nello spazio di lavoro.',
      confirmUnshareDescription:
        'I membri dello spazio di lavoro perderanno subito l’accesso al prompt, alla trascrizione, all’attività degli strumenti e al risultato.',
      confirmCancel: 'Annullare l’esecuzione attiva?',
      confirmCancelDescription:
        'L’esecuzione si fermerà al prossimo punto di annullamento sicuro.',
      actionCompleted: 'Pianificazione aggiornata',
      permissionDenied:
        'Puoi visualizzare questa pianificazione, ma solo il proprietario o un amministratore delle pianificazioni può modificarla.',
      needsAttention:
        'Questa esecuzione deve essere verificata prima di continuare il lavoro senza supervisione.',
      runDetails: 'Dettagli dell’esecuzione',
    },
    notifications: {
      completed: 'Esecuzione pianificata completata',
      failed: 'Esecuzione pianificata non riuscita',
      attention: 'L’esecuzione pianificata richiede attenzione',
      missed: 'L’esecuzione singola pianificata è stata persa',
      viewRun: 'Vedi esecuzione',
      empty: 'Nessuna notifica di esecuzione per ora.',
      unread: 'Da leggere',
      read: 'Letta',
      caughtUp: 'Hai visto tutte le notifiche.',
    },
    errors: {
      generic: 'Si è verificato un problema. Riprova.',
      permission: 'Non hai l’autorizzazione per eseguire questa azione.',
      disabled:
        'Gli agenti pianificati non sono ancora abilitati per questo spazio di lavoro.',
      credits: 'Lo spazio di lavoro non dispone di crediti sufficienti.',
      limit:
        'Questo spazio di lavoro ha raggiunto il limite di pianificazioni attive.',
      runPending:
        'Per questa pianificazione è già in attesa o in corso un’esecuzione.',
      invalidTime: 'Scegli un orario futuro valido.',
      notFound: 'Questa pianificazione o esecuzione non è più disponibile.',
    },
  },
} satisfies Record<'fr' | 'it', ScheduledDetailedCopy>;
